import { useCallback /*useEffect, useMemo, useRef*/ } from 'react';
import { Path, useNavigate } from 'react-router-dom';
import {
    AuthStatus,
    BackKeyResult,
    Channel,
    CloseButtonResult,
    useAuthStatus,
    useBackKeyInterceptorStore,
    useCloseButtonInterceptorStore,
    useMenuAuthority,
    useMenuStore,
    usePageAttributesStore,
    useQuery,
    useRouterRegistry,
} from 'scbk-services';
import { meetsAuthRequirement } from 'scbk-services/auth/components/AuthRoute.tsx';
import { EventType, useEventBusEmit } from 'scbk-services/event-bus/index.ts';
import { useRestoreDataStore } from 'scbk-services/page/stores/restoreDataStore.ts';
import { PageAttributes } from 'scbk-services/types/page/page.js';
import {
    AuthorizableRouteObject,
    NavigateOptions,
    RouterBackOptions,
} from 'scbk-services/types/route/route.js';
import _ from 'scbk-services/utils/lodash-extension.ts';
import { RouterHistoryEntry, useRouterHistory } from '../stores/routerHistoryStore.ts';

/**
 * 해당 화면 지원채널 체크
 * @param currentChannel
 * @param allowed
 * @returns
 */
export const checkAllowedChannel = (
    currentChannel: string,
    allowed: string | Channel | Channel[] | null | undefined,
): boolean => {
    if (!currentChannel) {
        return false;
    }

    if (allowed == null) {
        return true;
    }

    const normalize = (v: string | Channel): string => {
        return typeof v === 'string' ? v : String(v).toUpperCase();
    };

    const curr = normalize(currentChannel);
    if (Array.isArray(allowed)) {
        return allowed.some((ch) => {
            const code = normalize(ch);
            if (code === '*') return true;
            return code === curr;
        });
    }

    const rule = normalize(allowed as any);
    if (rule === '*') return true;
    return rule === curr;
};

/**
 *
 * @param navigate
 * @returns
 */
export const useRouter = () => {
    // router registry store에서 route 관련 메타 정보 조회
    const { findModuleRouteObjectsByPageId } = useRouterRegistry.getState();
    const {
        targetBackRoute,
        reset,
        pushRoute,
        replaceRoute,
        popRoute,
        peekRoute,
        size: historySize,
        setStack,
    } = useRouterHistory.getState();
    const navigate = useNavigate();
    const { get: getBackKeyHandler } = useBackKeyInterceptorStore.getState();
    const { get: getCloseBtnHandler } = useCloseButtonInterceptorStore.getState();
    const { getStatus: getAuthStatus } = useAuthStatus();
    const emit = useEventBusEmit();
    const { mergeQuery } = useQuery();
    const { checkServiceTime } = useMenuAuthority();

    const setCurrentMenuItem = useMenuStore((state) => state.setCurrentMenuItem);
    const findByPath = useMenuStore((state) => state.findByPath);
    const clearRestoreData = useRestoreDataStore((state) => state.clearWithScreenId);
    const clearAllRestoreData = useRestoreDataStore((state) => state.clearAll);

    /**
     * 다음화면으로 이동한다.
     * @param id 이동할 화면ID
     * @param options 화면 이동처리 옵션
     * @returns
     */
    const processPushRoute = async (pageId: string, options?: NavigateOptions) => {
        if (!pageId) {
            return;
        }

        let routeObject = findModuleRouteObjectsByPageId(pageId);

        if (options?.routeData?.changeViewService) {
            routeObject = findModuleRouteObjectsByPageId(options?.routeData?.changeViewService);
        }

        if (!routeObject || routeObject === null) {
            console.error('# routeObject null or undefined', pageId);
            return;
        }

        //****************************************
        //화면별 이용시간 체크
        //화면 이동 시 forceCheckCode를 넘겨주면.. 해당 코드로 이용시간을 체크한다.
        //****************************************
        const forceCheckCode = options?.forceCheckCode ?? '';
        if (forceCheckCode) {
            await checkServiceTime(forceCheckCode);
        }

        const targetRoute = { ...routeObject };
        const hasPermission = meetsAuthRequirement(
            getAuthStatus(),
            targetRoute.requiredAuthLevel ?? AuthStatus.UNAUTHENTICATED,
        );

        //접근이 제한된 화면이라면.. EventType.NeedLogin 이벤트를 발생시킨다.
        if (!hasPermission) {
            emit(EventType.NeedLogin, {});
            return;
        }

        // clearHistory options이 true라면 stack 비우기
        if (options?.historyOptions?.clearHistory) {
            reset();
        }

        const module = targetRoute.module;
        const path = targetRoute.fullPath ?? '';
        const state = options?.state;
        const routeData = { ...(options?.routeData ?? {}), changeViewService: undefined };
        const routeType = options?.routeType ?? 'router';
        const query = mergeQuery(location.search, options?.query);

        const entry: RouterHistoryEntry = {
            id: targetRoute.id ?? '',
            module,
            path: path ?? '',
            state,
            routeData,
            query,
            routeType,
        };

        pushRoute({ ...entry });

        //2026.07.24 페이지 속성에 따른 스택 정리
        if (options?.routeType === 'menu') {
            clearBaseStack();
        }

        let to = {
            pathname: path,
        } as Path;

        if (options?.query) {
            to = {
                pathname: path,
                search: query,
            } as Path;
        }

        // menu route를 통해 들어왔을경우 create한 router를 이용해 실제 route
        if (options?.routeType === 'menu' && !options?.routeData?.changeViewService) {
            setTimeout(() => {
                navigate(to, {
                    ...options,
                    state: state ? { ...state } : {},
                });
            }, 0);
        }
    };

    /**
     * 현재화면을 설정한 화면ID로 교체한다.
     * @param id 교체할 화면ID
     * @param options 화면 이동처리 옵션
     */
    const processReplaceRoute = async (pageId: string, options?: NavigateOptions) => {
        if (!pageId) {
            return;
        }

        let routeObject = findModuleRouteObjectsByPageId(pageId);

        if (options?.routeData?.changeViewService) {
            routeObject = findModuleRouteObjectsByPageId(options?.routeData?.changeViewService);
        }

        if (!routeObject || routeObject === null) {
            console.error('# target route null or undefined', pageId);
            return;
        }

        //****************************************
        //화면별 이용시간 체크
        //화면 이동 시 forceCheckCode를 넘겨주면.. 해당 코드로 이용시간을 체크한다.
        //****************************************
        const forceCheckCode = options?.forceCheckCode ?? '';
        if (forceCheckCode) {
            await checkServiceTime(forceCheckCode);
        }

        const targetRoute: AuthorizableRouteObject = { ...routeObject };
        const hasPermission = meetsAuthRequirement(
            getAuthStatus(),
            targetRoute.requiredAuthLevel ?? AuthStatus.UNAUTHENTICATED,
        );

        //접근이 제한된 화면이라면.. EventType.NeedLogin 이벤트를 발생시킨다.
        if (!hasPermission) {
            emit(EventType.NeedLogin, {});
            return;
        }

        // clearHistory options이 true라면 stack 비우기
        if (options?.historyOptions?.clearHistory) {
            reset();
        }

        const path = targetRoute.fullPath ?? '';
        const state = options?.state;

        const id = targetRoute.id ?? '';
        const module = targetRoute.module;
        const routeData = { ...(options?.routeData ?? {}), changeViewService: undefined };
        const routeType = options?.routeType ?? 'router';
        const query = mergeQuery(location.search, options?.query);

        const entry: RouterHistoryEntry = {
            id,
            module,
            path,
            state,
            routeData,
            routeType,
            query,
        };

        replaceRoute({ ...entry });

        //2026.07.24 페이지 속성에 따른 스택 정리
        if (options?.routeType === 'menu') {
            clearBaseStack();
        }

        const from = location.pathname + location.search;

        let to = {
            pathname: path,
        } as Path;

        if (options?.query) {
            to = {
                pathname: path,
                search: query,
            } as Path;
        }

        if (options?.routeType === 'menu' && !options?.routeData?.changeViewService) {
            setTimeout(() => {
                navigate(to, {
                    ...options,
                    replace: true,
                    state: state ? { ...state, from } : { from },
                });
            }, 0);
        }
    };

    /**
     * 화면을 뒤로 이동한다.
     * @param options
     */
    const processBackRoute = useCallback(
        async (options?: RouterBackOptions) => {
            if (historySize() === 0) {
                //TODO 스택이 없는경우 처리
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const isSkipBackKeyInterceptor = options?.isSkipBackKeyInterceptor ?? false;

            const backKeyHandler = getBackKeyHandler();
            if (backKeyHandler && !isSkipBackKeyInterceptor) {
                const result: BackKeyResult = await backKeyHandler();
                if (!result) {
                    return;
                }
            }

            //*****************************************
            //현재 스텝의 첫 메뉴 화면으로 이동처리
            //*****************************************
            const backToEntry = options?.backToEntry ?? false;
            if (backToEntry) {
                const historyStack = useRouterHistory.getState().stack;

                const arr = [...historyStack].reverse();
                const targetRouteEntry = arr.find((v) => v.routeType === 'menu');
                if (!targetRouteEntry) {
                    return;
                }

                //stack clear
                reset(targetRouteEntry);

                const path = targetRouteEntry.path;
                const query = targetRouteEntry.query ? `?${targetRouteEntry.query}` : '';

                setTimeout(() => {
                    navigate(
                        {
                            pathname: path,
                            search: query,
                        },
                        {
                            ...options,
                            replace: true,
                            state: targetRouteEntry?.state ?? '',
                        },
                    );
                }, 0);
                return;
            }

            //뒤로가기 시 변경 할 타겟 화면ID가 있다면
            const backTargetId = options?.backTargetId ?? '';
            if (_.isNotEmpty(backTargetId)) {
                //스택 popRoute 처리
                const pop = popRoute();

                // pop 이후 최신 route entry 정보를 가져와 이동
                const routeEntry = peekRoute();

                if (!routeEntry) {
                    //다시 복원 처리
                    if (pop) {
                        pushRoute(pop);
                    }
                    emit(EventType.RouteHistoryEmpty);
                    return;
                }

                targetBackRoute(backTargetId);

                const targetRouteEntry = options ? peekRoute(options) : peekRoute();

                if (targetRouteEntry?.routeType === 'menu') {
                    //const path = `${targetRouteEntry.path}?${targetRouteEntry.query ? targetRouteEntry.query : ''}`;
                    const path = targetRouteEntry.path;
                    const query = targetRouteEntry.query ? `?${targetRouteEntry.query}` : '';

                    const backMenuItem = findByPath(targetRouteEntry.path);

                    if (backMenuItem) {
                        setCurrentMenuItem(backMenuItem);
                    }

                    setTimeout(() => {
                        navigate(
                            {
                                pathname: path,
                                search: query,
                            },
                            {
                                ...options,
                                replace: true,
                                state: targetRouteEntry?.state ?? '',
                            },
                        );
                    }, 0);
                }
            } else {
                const step = Math.abs(options?.step ?? 1);

                let lastRoute: RouterHistoryEntry | null | undefined = null;
                for (let i = 0; i < step; i++) {
                    lastRoute = popRoute();
                    if (!lastRoute) {
                        emit(EventType.RouteHistoryEmpty);
                        return;
                    }

                    //******************************************
                    //restore state 있는지 확인하고 삭제한다.
                    //******************************************
                    //clearRestoreData(lastRoute.id);
                }

                // pop 이후 최신 route entry 정보를 가져와 이동
                const routeEntry = options ? peekRoute(options) : peekRoute();
                if (!routeEntry) {
                    emit(EventType.RouteHistoryEmpty);
                    return;
                }

                const { path, state, routeType, query } = routeEntry;

                if (routeType === 'menu') {
                    const backMenuItem = findByPath(path);

                    if (backMenuItem) {
                        setCurrentMenuItem(backMenuItem);
                    }

                    setTimeout(() => {
                        navigate(
                            {
                                pathname: path,
                                search: query ? `?${query}` : '',
                            },
                            {
                                ...options,
                                replace: true,
                                state,
                            },
                        );
                    }, 0);
                }
            }
        },
        [navigate, popRoute, peekRoute, historySize, emit, clearRestoreData],
    );

    /**
     * 닫기버튼용 back 처리
     */
    const processFirstBackRoute = useCallback(
        async (options?: RouterBackOptions) => {
            const historyStack = useRouterHistory.getState().stack;
            const length = historyStack.length;

            if (length === 0) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const isSkipCloseButtonInterceptor = options?.isSKipCloseButtonInterceptor ?? false;
            const closeButtonHandler = getCloseBtnHandler();
            if (closeButtonHandler && !isSkipCloseButtonInterceptor) {
                const result: CloseButtonResult = await closeButtonHandler();
                if (!result) {
                    return;
                }
            }

            const currentRouteEntry = options ? peekRoute(options) : peekRoute();
            if (!currentRouteEntry) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const { endPage = 'N' } =
                getPageAttribute(currentRouteEntry.id) ?? ({} as PageAttributes);

            if (endPage === 'Y') {
                clearAllRestoreData();
            }

            //스택이 한개인경우
            if (length === 1) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            let targetIndex = length - 2;
            let previousRoute = historyStack[targetIndex];
            while (targetIndex >= 0) {
                const { entryPage = 'N' } =
                    getPageAttribute(previousRoute.id) ?? ({} as PageAttributes);
                if (entryPage === 'Y') {
                    break;
                }

                targetIndex--;

                if (targetIndex < 0) {
                    //targetIndex = 0;
                    //[2026.08.13 최성돈] entry page를 찾지 못했다면.. RouteHistoryEmpty 이벤트를 발행한다.
                    emit(EventType.RouteHistoryEmpty);
                    break;
                }

                previousRoute = historyStack[targetIndex];
            }

            const cnt = targetIndex - (length - 1);
            const step = Math.abs(cnt);

            let lastRoute: RouterHistoryEntry | null | undefined = null;
            for (let i = 0; i < step; i++) {
                lastRoute = popRoute();
                if (!lastRoute) {
                    emit(EventType.RouteHistoryEmpty);
                    return;
                }

                //******************************************
                //restore state 있는지 확인하고 삭제한다.
                //******************************************
                //clearRestoreData(lastRoute.id);
            }

            // pop 이후 최신 route entry 정보를 가져와 이동
            const routeEntry = options ? peekRoute(options) : peekRoute();
            if (!routeEntry) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const { path, state, routeType, query } = routeEntry;

            if (routeType === 'menu') {
                const backMenuItem = findByPath(path);

                if (backMenuItem) {
                    setCurrentMenuItem(backMenuItem);
                }

                setTimeout(() => {
                    navigate(
                        {
                            pathname: path,
                            search: query ? `?${query}` : '',
                        },
                        {
                            ...options,
                            replace: true,
                            state,
                        },
                    );
                }, 0);
            }
        },
        [historySize, emit, getCloseBtnHandler, clearRestoreData],
    );

    /**
     * 뒤로가기용 back 처리
     */
    const processSkipBackRoute = useCallback(
        async (options?: RouterBackOptions) => {
            if (historySize() === 0) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const currentRouteEntry = options ? peekRoute(options) : peekRoute();
            if (!currentRouteEntry) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const historyStack = useRouterHistory.getState().stack;

            const isSkipBackKeyInterceptor = options?.isSkipBackKeyInterceptor ?? false;
            const backKeyHandler = getBackKeyHandler();

            const isSkipCloseButtonInterceptor = options?.isSKipCloseButtonInterceptor ?? false;
            const closeButtonHandler = getCloseBtnHandler();

            let cnt = -1;
            const length = historySize();
            let targetIndex = length - 2;
            let previousRoute = historyStack[length - 2];

            const pageAttributes = getPageAttribute(currentRouteEntry.id);
            if (pageAttributes?.endPage === 'Y') {
                if (closeButtonHandler && !isSkipCloseButtonInterceptor) {
                    const result: CloseButtonResult = await closeButtonHandler();
                    if (!result) {
                        return;
                    }
                } else {
                    //스택이 한개인경우
                    if (historySize() === 1) {
                        emit(EventType.RouteHistoryEmpty);
                        return;
                    }

                    const length = historySize();
                    while (targetIndex >= 0) {
                        const {
                            firstPage = 'N',
                            skipPage = 'N',
                            entryPage = 'N',
                        } = (getPageAttribute(previousRoute.id) ?? {}) as PageAttributes;

                        if (firstPage === 'Y' && skipPage !== 'Y') {
                            break;
                        }

                        if (entryPage === 'Y') {
                            break;
                        }

                        targetIndex--;

                        if (targetIndex < 0) {
                            targetIndex = 0;
                            break;
                        }

                        previousRoute = historyStack[targetIndex];
                    }

                    cnt = targetIndex - (length - 1);
                }
            } else if (backKeyHandler) {
                if (backKeyHandler && !isSkipBackKeyInterceptor) {
                    const result: BackKeyResult = await backKeyHandler();
                    if (!result) {
                        return;
                    } else {
                        //스택이 한개인경우
                        if (historySize() === 1) {
                            emit(EventType.RouteHistoryEmpty);
                            return;
                        }

                        cnt = targetIndex - (length - 1);
                    }
                }
            } else {
                //스택이 한개인경우
                if (historySize() === 1) {
                    emit(EventType.RouteHistoryEmpty);
                    return;
                }

                while (isSkipPage(previousRoute)) {
                    previousRoute = historyStack[length + cnt];
                    cnt--;
                }
            }

            const step = Math.abs(cnt);

            let lastRoute: RouterHistoryEntry | null | undefined = null;
            for (let i = 0; i < step; i++) {
                lastRoute = popRoute();
                if (!lastRoute) {
                    emit(EventType.RouteHistoryEmpty);
                    return;
                }

                //******************************************
                //restore state 있는지 확인하고 삭제한다.
                //******************************************
                //clearRestoreData(lastRoute.id);
            }

            // pop 이후 최신 route entry 정보를 가져와 이동
            const routeEntry = options ? peekRoute(options) : peekRoute();
            if (!routeEntry) {
                emit(EventType.RouteHistoryEmpty);
                return;
            }

            const { path, state, routeType, query } = routeEntry;

            if (routeType === 'menu') {
                const backMenuItem = findByPath(path);

                if (backMenuItem) {
                    setCurrentMenuItem(backMenuItem);
                }

                setTimeout(() => {
                    navigate(
                        {
                            pathname: path,
                            search: query ? `?${query}` : '',
                        },
                        {
                            ...options,
                            replace: true,
                            state,
                        },
                    );
                }, 0);
            }
        },
        [emit, historySize, getBackKeyHandler, clearRestoreData],
    );

    /**
     * 메뉴 요청인 경우 stack 정리
     */
    const clearBaseStack = useCallback(async () => {
        const lastRoute = peekRoute();
        if (lastRoute == null) {
            return;
        }
        const lastIsEntryPage = isEntryPageYn(lastRoute);
        const lastIsExclusivePage = isExclusivePageYn(lastRoute);

        const historyStack = useRouterHistory.getState().stack;
        const newStack: RouterHistoryEntry[] = [...historyStack];
        const removeIndexes: number[] = [];

        historyStack.forEach((entry, index) => {
            if (historyStack.length - 1 !== index) {
                if (lastIsEntryPage && lastIsExclusivePage) {
                    //newStack.slice(index, 1);
                    clearRestoreData(entry.id);
                    removeIndexes.push(index);
                } else {
                    const { entryPage = 'N', exclusivePage = 'N' } = getPageAttribute(
                        entry.id,
                    ) as PageAttributes;
                    const result = entryPage === 'Y' || exclusivePage === 'Y';
                    if (!result) {
                        //newStack.slice(index, 1);
                        clearRestoreData(entry.id);
                        removeIndexes.push(index);
                    }
                }
            }
        });

        removeIndexes.reverse().forEach((index) => {
            newStack.splice(index, 1);
        });

        setStack(newStack);
    }, [peekRoute, setStack]);

    /**
     * 기점 페이지 여부
     * @param entry
     * @returns
     */
    const isEntryPageYn = (entry: RouterHistoryEntry) => {
        const { entryPage = 'N' } = (getPageAttribute(entry.id) ?? {}) as PageAttributes;
        return entryPage === 'Y';
    };

    /**
     * 유일 기점 페이지 여부
     * @param entry
     * @returns
     */
    const isExclusivePageYn = (entry: RouterHistoryEntry) => {
        const { exclusivePage = 'N' } = (getPageAttribute(entry.id) ?? {}) as PageAttributes;
        return exclusivePage === 'Y';
    };

    /**
     *
     * @param entry
     * @returns
     */
    const isSkipPage = (entry: RouterHistoryEntry) => {
        const { skipPage = 'N' } = (getPageAttribute(entry.id) ?? {}) as PageAttributes;
        return skipPage === 'Y';
    };

    /**
     *
     * @param id
     * @returns
     */
    const getPageAttribute = (id: string) => {
        const attr = usePageAttributesStore.getState().get(id);
        return attr;
    };

    return {
        goto: processPushRoute,
        push: processPushRoute,
        back: processBackRoute,
        firstBack: processFirstBackRoute,
        skipBack: processSkipBackRoute,
        replace: processReplaceRoute,
        mergeQuery,
        getQueryParamsAsObject: <T>(): T => {
            const searchParams = new URLSearchParams(window.location.search);
            return Object.fromEntries(searchParams.entries()) as T;
        },
    };
};
